/**
 * Club Dashboard - Club admin main dashboard
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Building, Settings, Users, Shield, Trophy, CheckSquare, Calendar as CalendarIcon, ClipboardList, UserPlus, Image, Zap } from 'lucide-react';
import Button from '../../components/Button';
import apiService from '../../services/api';
import { Card, AdminPageLayout, Section } from '../../components';
import StatCard from '../../components/StatCard';
import StatGrid from '../../components/StatGrid';

const ClubDashboard = () => {
  const [clubData, setClubData] = useState(null);
  const [stats, setStats] = useState({
    total_users: 0,
    total_coaches: 0,
    total_programs: 0,
    active_users: 0,
    total_locations: 0,
    active_locations: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
    loadInboxTasks();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // Load user info to get club_id and then fetch club data
      const userInfo = await apiService.getCurrentUser();
      const club_id = userInfo.club_id;

      if (club_id) {
        // Use new context-based endpoint - club from Host/X-Club-ID header
        const clubResponse = await apiService.get('/api/clubs/public/club-info');
        setClubData({
          id: club_id,
          name: clubResponse.name || clubResponse.display_name || 'Club Name',
          club_id: club_id
        });

        // Fetch real stats from backend
        try {
          const statsResponse = await apiService.getClubStats();

          // Fetch location count if club ID is available
          let locationCount = 0;
          let activeLocationCount = 0;
          try {
            // Don't pass club_id - use JWT token context for club admin
            const locations = await apiService.getClubBranches(null, { status: 'active' });
            activeLocationCount = locations?.length || 0;
            const allLocations = await apiService.getClubBranches();
            locationCount = allLocations?.length || 0;
          } catch (locErr) {
            console.error('Error loading locations:', locErr);
          }

          setStats({
            total_users: statsResponse.total_users || 0,
            total_coaches: statsResponse.total_coaches || 0,
            total_programs: statsResponse.total_programs || 0,
            active_users: statsResponse.active_users || 0,
            total_locations: locationCount,
            active_locations: activeLocationCount
          });
        } catch (statsErr) {
          console.error('Error loading stats:', statsErr);
          // Fall back to zeros if stats fail
          setStats({
            total_users: 0,
            total_coaches: 0,
            total_programs: 0,
            active_users: 0,
            total_locations: 0,
            active_locations: 0
          });
        }
      } else {
        setClubData({
          name: 'Club Name',
          tenant_id: null
        });
        setStats({
          total_users: 0,
          total_coaches: 0,
          total_programs: 0,
          active_users: 0
        });
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Load pending tasks for club admin
  const loadInboxTasks = async () => {
    try {
      setTasksLoading(true);
      const response = await apiService.getInboxTasks();
      setTasks(response.tasks?.filter(task => task.role === 'club_admin') || []);
    } catch (err) {
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="club-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="club-dashboard">
        <div className="error-message">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <Button onClick={loadDashboardData}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const actionCards = [
    {
      to: '/admin/branches',
      icon: <Building size={32} />,
      title: 'BRANCH MANAGEMENT',
      description: 'View and manage club locations'
    },
    {
      to: '/admin/club/settings',
      icon: <Settings size={32} />,
      title: 'CLUB SETTINGS',
      description: 'Configure age policy and team settings'
    },
    {
      to: '/admin/users',
      icon: <Users size={32} />,
      title: 'USER MANAGEMENT',
      description: 'View, add, and manage all club members'
    },
    {
      to: '/admin/roles',
      icon: <Shield size={32} />,
      title: 'ROLE MANAGEMENT',
      description: 'Configure club roles and permissions'
    },
    {
      to: '/admin/skills',
      icon: <Trophy size={32} />,
      title: 'SKILL MANAGEMENT',
      description: 'Create skills, packages, and assignments'
    },
    {
      to: '/admin/programs',
      icon: <CheckSquare size={32} />,
      title: 'PROGRAM MANAGEMENT',
      description: 'Create and manage training programs'
    },
    {
      to: '/admin/templates',
      icon: <Zap size={32} />,
      title: 'TRAINING TEMPLATES',
      description: 'Manage session and activity templates'
    },
    {
      to: '/admin/seasons',
      icon: <CalendarIcon size={32} />,
      title: 'SEASON MANAGEMENT',
      description: 'Configure club seasons and time periods'
    },
    {
      to: '/admin/calendars',
      icon: <ClipboardList size={32} />,
      title: 'CALENDAR MANAGEMENT',
      description: 'Configure program schedules and events'
    },
    {
      to: '/admin/tryouts',
      icon: <UserPlus size={32} />,
      title: 'TRYOUT MANAGEMENT',
      description: 'Handle tryout registrations, evaluations, and offers',
      disabled: true
    },
    {
      to: '/admin/documents',
      icon: <ClipboardList size={32} />,
      title: 'DOCUMENT MANAGEMENT',
      description: 'Club waivers, forms, and document templates'
    },
    {
      to: '/admin/gallery',
      icon: <Image size={32} />,
      title: 'GALLERY MANAGEMENT',
      description: 'Upload and manage club photos and images'
    }
  ];

  return (
    <AdminPageLayout
      title="CLUB DASHBOARD"
      subtitle="Overview of club operations and quick access to administrative functions"
      backButton={false}
      maxWidth="1400px"
    >
      {/* Pending Actions */}
      {tasks.length > 0 && (
        <Section title="PENDING ACTIONS">
          <Card level={1}>
            {tasksLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading pending actions...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {tasks.map(task => (
                  <Card key={task.id} level={2}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{task.title}</div>
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>{task.description}</div>
                      </div>
                      <Button onClick={() => navigate(task.link)} size="sm">
                        {task.actionLabel || 'VIEW'}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </Section>
      )}

      {/* Statistics Overview */}
      <Section title="STATISTICS">
        <StatGrid>
          <StatCard
            number={stats.total_locations}
            label="Locations"
            detail={`${stats.active_locations} active`}
          />
          <StatCard
            number={stats.total_users}
            label="Total Users"
            detail={`${stats.active_users} active`}
          />
          <StatCard
            number={stats.total_coaches}
            label="Coaches"
            detail="All levels"
          />
          <StatCard
            number={stats.total_programs}
            label="Programs"
            detail="Active programs"
          />
        </StatGrid>
      </Section>

      {/* Quick Actions */}
      <Section title="QUICK ACTIONS">
        <Card level={1}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {actionCards.map((action, idx) => {
              const ActionWrapper = action.disabled ? 'div' : Link;
              const wrapperProps = action.disabled ? {} : { to: action.to };
              
              return (
                <ActionWrapper
                  key={idx}
                  {...wrapperProps}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    filter: action.disabled ? 'grayscale(100%)' : 'none',
                    opacity: action.disabled ? 0.6 : 1,
                    cursor: action.disabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Card
                    level={2}
                    style={{
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: action.disabled ? 'not-allowed' : 'pointer',
                      pointerEvents: action.disabled ? 'none' : 'auto'
                    }}
                  onMouseEnter={(e) => {
                    if (!action.disabled) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!action.disabled) {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: '1rem'
                  }}>
                    <div style={{
                      color: 'var(--color-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '64px',
                      height: '64px'
                    }}>
                      {action.icon}
                    </div>
                    <div>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        marginBottom: '0.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {action.title}
                      </div>
                      <div style={{
                        color: 'var(--color-text-secondary)',
                        fontSize: '0.9rem'
                      }}>
                        {action.description}
                      </div>
                    </div>
                  </div>
                </Card>
              </ActionWrapper>
            );
            })}
          </div>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

export default ClubDashboard;